import Head from 'next/head'
import Image from 'next/image'

import { Masthead, MastheadLogo, Root } from './cartier-bracelets.style'
import Button from './cartier-bracelets/Button'
import Header from './cartier-bracelets/Header'
import TAImage from './cartier-bracelets/Image'
import ImageCarousel from './cartier-bracelets/ImageCarousel'
import Slice from './cartier-bracelets/Slice'
import Text from './cartier-bracelets/Text'
import galleryImage1 from './cartier-bracelets/gallery/1.jpg'
import galleryImage2 from './cartier-bracelets/gallery/2.jpg'
import galleryImage3 from './cartier-bracelets/gallery/3.jpg'
import galleryImage4 from './cartier-bracelets/gallery/4.jpg'
import galleryImage5 from './cartier-bracelets/gallery/5.jpg'
import galleryImage6 from './cartier-bracelets/gallery/6.jpg'
import Logo from './cartier-bracelets/logo.svg'
import mastheadSm from './cartier-bracelets/masthead-sm.jpg'
import masthead from './cartier-bracelets/masthead.jpg'
import materials1 from './cartier-bracelets/materials1.jpg'
import materials2 from './cartier-bracelets/materials2.jpg'
import materialsSm from './cartier-bracelets/materials-sm.jpg'
import personalisationSm from './cartier-bracelets/personalisation-sm.jpg'
import personalisation from './cartier-bracelets/personalisation.jpg'
import sevenLinksSm from './cartier-bracelets/seven-links-sm.jpg'
import sevenLinks from './cartier-bracelets/seven-links.jpg'

const description =
  'Discover The Armoury’s exquisite 18k gold watch bracelets, inspired by vintage Cartier designs. Made-to-order in yellow, rose, white, or tri-colour gold, these bracelets feature solid gold end-links, a butterfly clasp, and optional engraved initials. Perfectly complementing the Tank Louis Cartier and Tank Cintrée, each bracelet is crafted with precision and elegance.'

const socialDescription =
  'Introducing our exceptional 18k gold bracelets, inspired by the timeless elegance of vintage Cartier designs. Crafted in solid yellow, rose, or white gold — with options for a tri-colour finish — each bracelet is made-to-order, perfectly complementing the iconic Tank Louis Cartier or Tank Cintrée. Featuring flush-fitting solid gold end-links, a secure butterfly clasp, and optional personalization like engraved initials, this is luxury redefined. Available for viewing at our Hong Kong store and soon in New York.  Pricing starts at HKD 144,000 / USD 18,500. Contact info@thearmoury.com to place your order.'

const sliceImageSizes =
  '(max-width: 575px) calc(100vw - 6rem), (max-width: 768px) calc(100vw - 6rem), calc(50vw - 12rem)'

export default function Page() {
  return (
    <>
      <Head>
        <title>Cartier Bracelets - The Armoury</title>
        <meta name="description" content={description} />
        {/* Facebook Meta Tags */}
        <meta property="og:url" content="https://www.thearmoury.com/watches/cartier-bracelets" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Cartier Bracelets" />
        <meta property="og:description" content={socialDescription} />
        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="watches.thearmoury.com" />
        <meta property="twitter:url" content="https://www.thearmoury.com/watches/cartier-bracelets" />
        <meta name="twitter:title" content="Cartier Bracelets" />
        <meta name="twitter:description" content={socialDescription} />

        <link rel="icon" type="image/png" href="/favicon-32x32.png" sizes="32x32" />
        <link rel="icon" type="image/png" href="/favicon-16x16.png" sizes="16x16" />
        <link rel="manifest" type="image/png" href="/manifest.json" />
        <link rel="mast-icon" href="/safari-pinned-tab.svg" />
        <link rel="shortcut icon" href="/favicon.ico" />
        <link rel="stylesheet" href="https://use.typekit.net/dbw1jpz.css" />
      </Head>

      <Root>
        <Masthead className="-mb-5 md:mb-0">
          <MastheadLogo>
            <Logo />
          </MastheadLogo>
          <picture>
            <source srcSet={mastheadSm.src} media="(max-width: 500px)" />
            <source srcSet={masthead.src} media="(min-width: 501px)" />
            <img src={masthead.src} alt="" />
          </picture>
        </Masthead>
        <div className="mx-auto max-w-watchesMaxFlush flex flex-col items-center justify-center text-center px-4 md:px-10 pb-20 md:pb-36">
          <Header className="mb-6 md:mb-8">A Cartier Tank in its finest attire</Header>
          <Text>
            <p className="max-w-[40rem]">
              Much like the tactile joy of a fine bespoke cashmere coat, a solid gold bracelet made to your wrist size
              is the ultimate way to enjoy your Cartier Tank.
            </p>
          </Text>
        </div>
        <div className="-mb-48 md:mb-0">
          <picture>
            <source srcSet={sevenLinksSm.src} media="(max-width: 500px)" />
            <source srcSet={sevenLinks.src} media="(min-width: 501px)" />
            <img src={sevenLinks.src} alt="" />
          </picture>
        </div>
        <Slice align="left" valign="top">
          <Header>Seven links</Header>
          <Text>
            <p>
              Inspired by the magnificent examples of Cartier from the past, our gold bracelets are made of solid 18k
              gold. Each link of the seven link row is shaped to match the design of the head of the watch: stout for
              the Tank Louis Cartier and slim for the Tank Cintrée. The bracelet is fitted with solid gold end-links
              that fit flush to the case of the watch and a butterfly clasp to secure the watch to your wrist.
            </p>
          </Text>
        </Slice>
        <Slice className="hidden md:block">
          <Image src={materials1} alt="" sizes={sliceImageSizes} />
          <Image src={materials2} alt="" sizes={sliceImageSizes} />
        </Slice>
        <div className="md:hidden -mb-48 md:mb-0">
          <TAImage src={materialsSm} flush={true} />
        </div>
        <Slice align="left" valign="top">
          <Header>Materials & Finishes</Header>
          <Text>
            <p>
              Our 18k gold bracelets are available in yellow, rose or white gold. For platinum Cartier watches, we
              recommend our brilliant rhodium plating which matches perfectly with platinum.
            </p>
            <p>
              We are also able to make a tri-colour gold bracelet as pictured below to match the special edition Mosaic
              Dial Louis Cartier from 2022.
            </p>
            <p>
              While we recommend our brushed finish to match the finish of the case at 6 and 9 o’clock, a polished
              finish is available on request.
            </p>
          </Text>
        </Slice>
        <div className="-mb-48 md:mb-0">
          <picture>
            <source srcSet={personalisationSm.src} media="(max-width: 500px)" />
            <source srcSet={personalisation.src} media="(min-width: 501px)" />
            <img src={personalisation.src} alt="" />
          </picture>
        </div>
        <Slice align="left" valign="top">
          <Header>Personalization</Header>
          <Text>
            <p>
              As our bracelets are made to order, it is possible to specify the length desired for the bracelet. Please
              note that for extra long bracelets, an additional charge is required.
            </p>
            <p>The clasp can also be engraved with the owners initials at no extra charge.</p>
          </Text>
        </Slice>
        <ImageCarousel
          images={[galleryImage1, galleryImage2, galleryImage3, galleryImage4, galleryImage5, galleryImage6]}
        />
        <Slice align="left" valign="top">
          <Header>How to purchase</Header>
          <Text>
            <p>
              We currently have samples of our bracelets available for viewing at our Hong Kong store in the Pedder
              Arcade. We will also have samples available for viewing at our New York stores in the near future.
            </p>
            <p>
              To place an order, please contact <a href="mailto:info@thearmoury.com">info@thearmoury.com</a> to discuss
              your requirements.
            </p>
            <p>
              Pricing for our bracelets starts at HKD 159,000 / USD 20,500 for the single colour and HKD190,000 / USD
              24,500 for the tri-colour. Payment terms are 50% deposit and 50% on delivery. All bracelets are
              made-to-order in Hong Kong with a delivery time of approximately four months.
            </p>
          </Text>
          <Button as="a" href="mailto:info@thearmoury.com">
            Contact
          </Button>
        </Slice>
      </Root>
    </>
  )
}

Page.getLayout = function getLayout(page) {
  return <div className="overflow-x-hidden">{page}</div>
}
